package com.milta.feature.auth.data.repository

import com.milta.core.auth.JwtParser
import com.milta.core.auth.SessionEvent
import com.milta.core.auth.SessionManager
import com.milta.core.auth.TokenStorage
import com.milta.core.mode.AppModeManager
import com.milta.core.phone.PhoneNumber
import com.milta.feature.auth.data.remote.AuthApi
import com.milta.feature.auth.data.remote.dto.AuthenticateWithOAuthRequest
import com.milta.feature.auth.data.remote.dto.LogoutRequest
import com.milta.feature.auth.data.remote.dto.OAuthAuthenticateResponse
import com.milta.feature.auth.data.remote.dto.OtpSendRequest
import com.milta.feature.auth.data.remote.dto.OtpVerifyRequest
import com.milta.feature.auth.data.remote.dto.RegisterWithOAuthRequest
import com.milta.feature.auth.data.remote.dto.UpdatePendingPhoneRequest
import com.milta.feature.auth.domain.entity.OAuthAuthenticateOutcome
import com.milta.feature.auth.domain.entity.OAuthProvider
import com.milta.feature.auth.domain.entity.UserRole
import com.milta.feature.auth.domain.repository.AuthRepository
import com.milta.feature.auth.domain.usecase.AvailabilityResult
import io.ktor.client.HttpClient
import io.ktor.client.plugins.auth.authProvider
import io.ktor.client.plugins.auth.providers.BearerAuthProvider
import kotlinx.coroutines.flow.SharedFlow

class AuthRepositoryImpl(
    private val api: AuthApi,
    private val tokenStorage: TokenStorage,
    private val sessionManager: SessionManager,
    private val appModeManager: AppModeManager,
    private val httpClient: HttpClient,
) : AuthRepository {

    // Ktor's bearer Auth plugin calls loadTokens() lazily on first use and caches the result for
    // every request after that — it never re-reads TokenStorage on its own. Without this, the
    // very first HttpClient request made pre-login (e.g. the OAuth exchange call itself) caches
    // "no token", and every authenticated request right after a fresh login (e.g. /routes,
    // /subscriptions/me) keeps reusing that stale empty cache and comes back 401, even though the
    // tokens were saved correctly.
    private fun refreshBearerTokenCache() {
        httpClient.authProvider<BearerAuthProvider>()?.clearToken()
    }

    override val sessionEvents: SharedFlow<SessionEvent> = sessionManager.events

    override suspend fun authenticateWithOAuth(
        provider: OAuthProvider,
        idToken: String,
    ): Result<OAuthAuthenticateOutcome> = runCatching {
        toOutcome(api.authenticateWithOAuth(AuthenticateWithOAuthRequest(provider = provider.apiValue, idToken = idToken)))
    }

    override suspend fun updatePendingPhone(
        provider: OAuthProvider,
        idToken: String,
        phoneNumber: String,
    ): Result<OAuthAuthenticateOutcome> = runCatching {
        toOutcome(
            api.updatePendingPhone(
                UpdatePendingPhoneRequest(
                    provider = provider.apiValue,
                    idToken = idToken,
                    phoneNumber = PhoneNumber.normalize(phoneNumber),
                )
            )
        )
    }

    private suspend fun toOutcome(response: OAuthAuthenticateResponse): OAuthAuthenticateOutcome = when {
        !response.isRegistered -> OAuthAuthenticateOutcome.NotRegistered
        !response.phoneVerified -> OAuthAuthenticateOutcome.PhoneVerificationRequired(
            phoneNumber = response.phoneNumber.orEmpty()
        )
        else -> {
            val auth = requireNotNull(response.auth) { "auth missing for phoneVerified response" }
            tokenStorage.saveTokens(auth.accessToken, auth.refreshToken)
            refreshBearerTokenCache()
            sessionManager.emit(SessionEvent.LoggedIn)
            val role = JwtParser.extractRole(auth.accessToken) ?: "Driver"
            appModeManager.setModeFromRole(role)
            OAuthAuthenticateOutcome.Authenticated(role)
        }
    }

    override suspend fun registerWithOAuth(
        provider: OAuthProvider,
        idToken: String,
        firstName: String,
        lastName: String,
        phoneNumber: String,
        organizationName: String?,
        role: UserRole,
        language: String?,
    ): Result<Unit> = runCatching {
        api.registerWithOAuth(
            RegisterWithOAuthRequest(
                provider = provider.apiValue,
                idToken = idToken,
                firstName = firstName,
                lastName = lastName,
                phoneNumber = PhoneNumber.normalize(phoneNumber),
                organizationName = organizationName,
                roles = listOf(role.apiValue),
                language = language,
            )
        )
        Unit
    }

    override suspend fun requestOtp(phoneNumber: String): Result<Unit> = runCatching {
        api.requestOtp(OtpSendRequest(phoneNumber = PhoneNumber.normalize(phoneNumber)))
    }

    override suspend fun verifyOtp(phoneNumber: String, code: String): Result<String> = runCatching {
        val tokenResponse = api.verifyOtp(OtpVerifyRequest(phoneNumber = PhoneNumber.normalize(phoneNumber), code = code))
        tokenStorage.saveTokens(tokenResponse.accessToken, tokenResponse.refreshToken)
        refreshBearerTokenCache()
        sessionManager.emit(SessionEvent.LoggedIn)
        val role = JwtParser.extractRole(tokenResponse.accessToken) ?: "Driver"
        appModeManager.setModeFromRole(role)
        role
    }

    override suspend fun checkAvailability(email: String?, phone: String?): Result<AvailabilityResult> =
        runCatching {
            val response = api.checkAvailability(email, phone?.let { PhoneNumber.normalize(it) })
            AvailabilityResult(emailTaken = response.emailTaken, phoneTaken = response.phoneTaken)
        }

    override suspend fun logout() {
        val refreshToken = tokenStorage.getRefreshToken()
        if (refreshToken != null) {
            runCatching { api.logout(LogoutRequest(refreshToken = refreshToken)) }
        }
        tokenStorage.clear()
        appModeManager.reset()
        sessionManager.emit(SessionEvent.LoggedOut)
    }

    override suspend fun logoutAll() {
        runCatching { api.logoutAll() }
        tokenStorage.clear()
        appModeManager.reset()
        sessionManager.emit(SessionEvent.LoggedOut)
    }

    override fun isAuthenticated(): Boolean = tokenStorage.getRefreshToken() != null
}
